import { createCanvas, destroyCanvas, drawImage } from "@/libs/canvas"
import type { FramePixels } from "@/libs/cv"
import type { Rect } from "@/libs/geometry/types"
import { mapValues } from "@/libs/lodash"

export type ImageSource = TexImageSource & CanvasImageSource

/** @return Display width and height of the given image source */
export const getImageSourceDimensions = (source: ImageSource) => {
  if (isVideoFrame(source)) return { width: source.displayWidth, height: source.displayHeight }
  if (isHTMLVideoElement(source)) return { width: source.videoWidth, height: source.videoHeight }
  if (isHTMLImageElement(source))
    return { width: source.naturalWidth, height: source.naturalHeight }

  return { width: source.width, height: source.height }
}

export const isVideoFrame = (obj: any): obj is VideoFrame =>
  typeof VideoFrame !== "undefined" && obj instanceof VideoFrame
export const isHTMLVideoElement = (obj: any): obj is HTMLVideoElement => obj?.nodeName === "VIDEO"
export const isHTMLImageElement = (obj: any): obj is HTMLImageElement => obj?.nodeName === "IMAGE"

/** Returns coordinates of visible rect of the original image source for the given object fit mode */
export const getVisibleRect = (
  image: HTMLCanvasElement | HTMLVideoElement,
  objectFit: "cover" | "contain" = "cover",
) => {
  const { width, height } = getImageSourceDimensions(image)

  if (objectFit === "cover") {
    const { clientWidth, clientHeight } = image

    const scale = Math.max(clientWidth / width, clientHeight / height)
    const px = (width - clientWidth / scale) / 2
    const py = (height - clientHeight / scale) / 2

    return {
      x: Math.max(0, Math.round(px)),
      y: Math.max(0, Math.round(py)),
      width: Math.min(Math.round(width - 2 * px), width),
      height: Math.min(Math.round(height - 2 * py), height),
    }
  }

  return { x: 0, y: 0, width, height }
}

/**
 * Fits the given width and height into the given resolution.
 * E.g given 480p resolution, the function will fit the smallest dimension into 480px
 * and adjust the second one accordingly while keeping the aspect ratio.
 */
export const fit = ({ width, height }: { width: number; height: number }, resolution: number) => {
  const scale = resolution / Math.min(width, height)

  return mapValues({ width, height }, (size) => Math.floor(size * scale))
}

export const createImage = async (src: string): Promise<HTMLImageElement> => {
  const img = new Image()

  await new Promise<void>((resolve, reject) => {
    img.addEventListener("load", () => resolve(), { once: true })
    img.addEventListener("error", () => reject(new Error("Failed to load the image file")), {
      once: true,
    })
    img.src = src
  })

  return img
}
export const destroyImage = (img: HTMLImageElement) => {
  URL.revokeObjectURL(img.src)
  img.src = ""
}

export const blobToImage = async (
  blob: Blob,
  {
    maxSize = 4096,
  }: {
    /**
     * The upper limit for the returned image width and height.
     * If at least on of the input dimensions is larger than the given `maxSize`,
     * it will be downscaled to fit into the `maxSize` preserving the input‘s aspect ratio.
     */
    maxSize?: number
  } = {},
): Promise<HTMLImageElement> => {
  // we have to use the Image API since it handles rotation of iOS photos compared for example to createImageBitmap
  const img = await createImage(URL.createObjectURL(blob))

  if (!maxSize || maxSize < 1 || [img.width, img.height].every((dim) => dim <= maxSize)) return img

  const scale = maxSize / Math.max(img.width, img.height)
  const image = await imageBitmapToImage(
    await createImageBitmap(img, {
      resizeWidth: img.width * scale,
      resizeHeight: img.height * scale,
      resizeQuality: "high",
    }),
  )

  destroyImage(img)

  return image
}

export const imageBitmapToImage = async (
  imageBitmap: ImageBitmap,
  { rect }: { rect?: Rect } = {},
): Promise<HTMLImageElement> => {
  const canvas = createCanvas()

  drawImage(canvas, imageBitmap, { rect })

  const blob = await canvas.convertToBlob({ type: "image/png", quality: 1 })
  const img = await createImage(URL.createObjectURL(blob))

  destroyCanvas(canvas)

  return img
}

export type ImageFormat = "jpeg" | "png" | "webp"

export const imageToBlob = async (
  image: HTMLImageElement,
  options?: { format?: ImageFormat; horizontalFlip?: boolean },
): Promise<Blob> => {
  const canvas = createCanvas()

  drawImage(canvas, image, { horizontalFlip: options?.horizontalFlip })

  try {
    return await canvas.convertToBlob({ type: `image/${options?.format ?? "jpeg"}`, quality: 1 })
  } finally {
    destroyCanvas(canvas)
  }
}

/**
 * Hack for converting large images to pixels without webgl2 buffer issues.
 * Is used in processing 'upload photo' image-source.
 * @param source Image source to convert to pixels.
 * @returns Pixels of the image source.
 */
export const imageBitmapToPixels = (source: ImageBitmap): FramePixels => {
  const canvas = createCanvas(source.width, source.height)
  const pixels = new Uint8Array(source.width * source.height * 4)
  const ctx = canvas.getContext("2d")!
  ctx.drawImage(source, 0, 0)
  const imageData = ctx.getImageData(0, 0, source.width, source.height)
  pixels.set(imageData.data)
  return { data: pixels, width: source.width, height: source.height, format: "RGBA" }
}
